import { registerLocaleData } from '@angular/common';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import localeDe from '@angular/common/locales/de';
import localeEn from '@angular/common/locales/en';
import {
  ApplicationConfig,
  inject,
  LOCALE_ID,
  provideBrowserGlobalErrorListeners,
  provideZonelessChangeDetection,
} from '@angular/core';
import {
  MAT_MOMENT_DATE_ADAPTER_OPTIONS,
  MomentDateAdapter,
} from '@angular/material-moment-adapter';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import {
  provideClientHydration,
  withEventReplay,
  withI18nSupport,
} from '@angular/platform-browser';
import {
  createUrlTreeFromSnapshot,
  isActive,
  NavigationError,
  provideRouter,
  Router,
  withComponentInputBinding,
  withInMemoryScrolling,
  withNavigationErrorHandler,
  withRouterConfig,
  withViewTransitions,
} from '@angular/router';
import { environment } from '@environment';
import { provideTranslateService } from '@ngx-translate/core';
import { provideSwiper } from '@shared/components/swiper';
import { I18nService } from '@shared/core/i18n.service';
import { acceptLanguageInterceptor } from '@shared/core/interceptors/accept-language.interceptor';
import { accessTokenInterceptor } from '@shared/core/interceptors/access-token.interceptor';
import { apiPrefixInterceptor } from '@shared/core/interceptors/api-prefix.interceptor';
import { provideSearchConnectors, UsersSearchConnector } from '@shared/global-search';
import { ContactsSearchConnector } from './features/global-search/connectors/contacts-search.connector';
import { ContractorsSearchConnector } from './features/global-search/connectors/contractors-search.connector';
import { PropertiesSearchConnector } from './features/global-search/connectors/properties-search.connector';
import { UnitsSearchConnector } from './features/global-search/connectors/units-search.connector';
import { DocumentsSearchConnector } from './features/global-search/connectors/documents-search.connector';
import { EmailsSearchConnector } from './features/global-search/connectors/emails-search.connector';
import { TasksSearchConnector } from './features/global-search/connectors/tasks-search.connector';
import { AUTHENTICATION } from '@shared/tokens/authentication.token';
import { ENVIRONMENT } from '@shared/tokens/environment.token';
import { OAUTH_CONFIG } from '@shared/tokens/oauth-config.token';
import { provideOAuthClient } from 'angular-oauth2-oidc';
import { RECAPTCHA_V3_SITE_KEY } from 'ng-recaptcha-2';
import { routes } from './app.routes';
import { AuthenticationService } from './core/authentication/authentication.service';
import { getAuthConfig } from './core/providers/oauth-config.provider';
import { RoutesSearchConnector } from './features/global-search/connectors/routes-search.connector';
import { mockApiInterceptor } from '../../tests/fixtures/mock-api.interceptor';
import { provideMockApi } from '../../tests/fixtures/mock-api.provider';

registerLocaleData(localeDe);
registerLocaleData(localeEn);

export function getCurrentLanguage(i18nService: I18nService) {
  i18nService.init(environment.supportedLanguages);
  return i18nService.language;
}

export const MY_FORMATS = {
  parse: {
    dateInput: 'DD.MM.YYYY',
    timeInput: 'HH:mm',
  },
  display: {
    dateInput: 'DD.MM.YYYY',
    monthYearLabel: 'MMM YYYY',
    dateA11yLabel: 'LL',
    monthYearA11yLabel: 'MMMM YYYY',
    timeInput: 'HH:mm',
    timeOptionLabel: 'HH:mm',
  },
};

export const MY_DATE_LOCALE = 'de-DE';

export const appConfig: ApplicationConfig = {
  providers: [
    { provide: ENVIRONMENT, useValue: environment },
    provideZonelessChangeDetection(),
    provideBrowserGlobalErrorListeners(),
    provideRouter(
      routes,
      withInMemoryScrolling(),
      withViewTransitions({
        onViewTransitionCreated: ({ transition, to }) => {
          const router = inject(Router);
          const toTree = createUrlTreeFromSnapshot(to, []);
          const shouldSkip = isActive(toTree, router, {
            paths: 'exact',
            matrixParams: 'exact',
            fragment: 'ignored',
            queryParams: 'ignored',
          });

          // Skip the transition if the only thing changing is the fragment and queryParams
          if (shouldSkip()) {
            transition.skipTransition();
          }
        },
      }),
      withNavigationErrorHandler((error: NavigationError) => {
        const router = inject(Router);
        if (error?.error) {
          console.error('Navigation error occurred:', error.error);
        }

        // STALE CHUNK after a deploy: the running app holds the OLD build manifest and asks
        // for hashed chunks the server no longer serves ("Failed to fetch dynamically
        // imported module …chunk-XXXX.js"). A hard reload fetches the new index.html and
        // heals it — guarded to ONE attempt per url so a genuinely broken build cannot
        // reload-loop the browser.
        const message = String((error?.error as Error)?.message ?? '');
        if (/Failed to fetch dynamically imported module|Importing a module script failed/i.test(message)) {
          // Strip the cache-bust param BEFORE keying the guard: error.url still carries the
          // _r of the previous attempt, so keying on the raw url made every attempt look like
          // a new url — the guard never matched and the reloads looped forever, appending one
          // more _r each pass.
          const url = new URL(error.url, window.location.origin);
          url.searchParams.delete('_r');

          const guardKey = `chunk-reload:${url.pathname}${url.search}`;
          const lastAttempt = Number(sessionStorage.getItem(guardKey) ?? 0);

          // EXPIRING guard (30s) instead of once-per-session: a reload that still hit a
          // CACHED old index.html must be allowed to try again later — while a genuinely
          // broken build still cannot reload-loop the browser.
          if (Date.now() - lastAttempt > 30_000) {
            sessionStorage.setItem(guardKey, String(Date.now()));

            // Cache-bust query: a bare reload may serve the SAME stale index.html from the
            // http cache — the changed url forces a fresh document (the router ignores
            // unknown query params). set() replaces instead of appending, so the url keeps
            // exactly one _r.
            url.searchParams.set('_r', String(Date.now()));
            window.location.assign(`${url.pathname}${url.search}${url.hash}`);
            return;
          }
        }

        router.navigate(['/error']);
      }),
      withComponentInputBinding(),
      withRouterConfig({ paramsInheritanceStrategy: 'always', onSameUrlNavigation: 'reload' }),
      // withPreloading(NoPreloading),
    ),
    provideClientHydration(withEventReplay(), withI18nSupport()),
    provideOAuthClient(),
    provideHttpClient(
      withInterceptors([
        accessTokenInterceptor,
        acceptLanguageInterceptor,
        apiPrefixInterceptor,
        // gRecaptchaTokenInterceptor,
        // authorizationInterceptor
        ...(environment.useMockApi ? [mockApiInterceptor] : []),
      ]),
    ),
    ...(environment.useMockApi ? [provideMockApi()] : []),
    provideTranslateService({
      fallbackLang: 'en',
      lang: 'en',
    }),
    { provide: LOCALE_ID, useFactory: getCurrentLanguage, deps: [I18nService] },
    { provide: OAUTH_CONFIG, useFactory: getAuthConfig, deps: [ENVIRONMENT] },
    { provide: AUTHENTICATION, useExisting: AuthenticationService },
    { provide: RECAPTCHA_V3_SITE_KEY, useValue: environment.recaptchaSiteKey },
    { provide: MAT_FORM_FIELD_DEFAULT_OPTIONS, useValue: { appearance: 'outline' } },
    { provide: DateAdapter, useClass: MomentDateAdapter, deps: [MAT_DATE_LOCALE] },
    { provide: MAT_MOMENT_DATE_ADAPTER_OPTIONS, useValue: { useUtc: true } },
    { provide: MAT_DATE_FORMATS, useValue: MY_FORMATS },
    { provide: MAT_DATE_LOCALE, useValue: MY_DATE_LOCALE },
    provideSwiper({
      options: { speed: 400 },
    }),
    provideSearchConnectors(
      RoutesSearchConnector,
      UsersSearchConnector,
      EmailsSearchConnector,
      TasksSearchConnector,
      DocumentsSearchConnector,
      ContractorsSearchConnector,
      PropertiesSearchConnector,
      UnitsSearchConnector,
      ContactsSearchConnector,
    ),
  ],
};